import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usersAPI } from '../lib/api';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { 
  User, 
  GitBranch, 
  Calendar,
  ArrowLeft,
  ExternalLink,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getProfile(userId);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="spinner" data-testid="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-slate-700">User not found</h2>
            <Button onClick={() => navigate('/')} className="mt-4">
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { user, stats, recent_questions, recent_nodes } = profile;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="profile-page">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Profile Header */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8 mb-6">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center text-3xl font-bold text-slate-500 overflow-hidden">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                user.name?.charAt(0).toUpperCase()
              )}
            </div>
            
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900">{user.name}</h1>
              <p className="text-slate-500">{user.email}</p>
              
              <div className="flex items-center gap-4 mt-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Joined {user.created_at ? format(new Date(user.created_at), 'MMM yyyy') : 'N/A'}
                </span>
                {user.role === 'admin' && (
                  <span className="badge badge-orange">Admin</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mt-8">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-slate-900">{stats.questions_created}</p>
              <p className="text-sm text-slate-500">Questions Created</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-slate-900">{stats.nodes_contributed}</p>
              <p className="text-sm text-slate-500">Nodes Contributed</p>
            </div>
          </div>
        </div>

        {/* Recent Questions */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-orange-500" />
            Recent Questions
          </h2>
          
          {recent_questions && recent_questions.length > 0 ? (
            <div className="space-y-3">
              {recent_questions.map((question) => (
                <Link
                  key={question.question_id}
                  to={`/q/${question.question_id}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                  data-testid={`question-link-${question.question_id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900 group-hover:text-orange-500 transition-colors">
                      {question.title}
                    </p>
                    <p className="text-sm text-slate-500">
                      {question.node_count} nodes • {question.contributor_count} contributors
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-orange-500 transition-colors" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">No questions yet</p>
          )}
        </div>

        {/* Recent Node Contributions */}
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-orange-500" />
            Recent Contributions
          </h2>
          
          {recent_nodes && recent_nodes.length > 0 ? (
            <div className="space-y-3">
              {recent_nodes.map((node) => (
                <Link
                  key={node.node_id}
                  to={`/node/${node.node_id}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                  data-testid={`node-link-${node.node_id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900 group-hover:text-orange-500 transition-colors">
                      {node.title}
                    </p>
                    <p className="text-sm text-slate-500">
                      {node.vote_score} votes • {formatDistanceToNow(new Date(node.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-orange-500 transition-colors" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">No contributions yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
